import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '@/context/TenantContext';
import { useWorkflowTitle } from '@/context/WorkflowTitleContext';
import { dashboardRoutes, supportRoutes } from '@/config/routes';

// Route title mappings for non-dashboard routes
const specialRouteTitles: Record<string, string> = {
    '/auth/login': 'Login',
    '/auth/signup': 'Sign Up',
    '/auth/confirm-email': 'Confirm Email',
    '/auth/forgot-password': 'Forgot Password',
    '/auth/reset-password': 'Reset Password',
    '/privacy-policy': 'Privacy Policy',
    '/terms-of-service': 'Terms of Service',
    '/whatsapp-onboarding-callback': 'WhatsApp Setup',
};

// Workflows sub-route title mappings
const workflowsSubRouteTitles: Record<string, string> = {
    'flows': 'Flows',
    'runs': 'Runs',
    'runs-search': 'Runs Search',
    'connections': 'Connections',
    'tables': 'Tables',
    'todos': 'Todos',
    'mcp': 'MCP Servers',
};

// Helper function to find route title by path
function getPageTitle(pathname: string): string | null {
    // Check special routes first
    if (specialRouteTitles[pathname]) {
        return specialRouteTitles[pathname];
    }
    
    // Handle workflows sub-routes specially
    if (pathname.startsWith('/dashboard/workflows/')) {
        const subRoute = pathname.replace('/dashboard/workflows/', '').split('/')[0].split('?')[0];
        if (workflowsSubRouteTitles[subRoute]) {
            return workflowsSubRouteTitles[subRoute];
        }
    }
    
    // Handle dashboard routes
    if (pathname.startsWith('/dashboard/')) {
        const segments = pathname.replace(/^\/dashboard\//, '').split('/');
        
        // Check dashboard routes
        for (const route of dashboardRoutes) {
            // Handle workflows/* pattern
            const routePath = route.path.replace('/*', '');
            if (segments[0] === routePath) {
                // If this is a child route
                if (segments.length > 1 && route.children) {
                    for (const child of route.children) {
                        if (segments[1] === child.path || 
                            (child.path.includes(':') && segments[1])) {
                            return child.title;
                        }
                    }
                }
                return route.title;
            }
        }
    }
    
    // Check support routes
    for (const route of supportRoutes) {
        if (pathname === route.path) {
            return route.title;
        }
    }
    
    return null;
}

export function DocumentHeadUpdater() {
    const { branding } = useTenant();
    const location = useLocation();
    const { workflowTitle } = useWorkflowTitle();

    useEffect(() => {
        // Remove any existing favicon links
        const existingLinks = document.querySelectorAll("link[rel*='icon']");
        existingLinks.forEach(link => link.remove());

        // Create new favicon link if we have branding
        if (branding?.favicon_url) {
            const link = document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = branding.favicon_url;
            document.head.appendChild(link);
        }

        // Get page title based on current route
        let pageTitle = getPageTitle(location.pathname);
        
        // Override with dynamic workflow title if available
        if (workflowTitle && location.pathname.startsWith('/dashboard/workflows/')) {
            pageTitle = workflowTitle;
        }
        
        // Format: "Page Name | Company Name"
        if (branding?.company_name) {
            if (pageTitle) {
                document.title = `${pageTitle} | ${branding.company_name}`;
            } else {
                document.title = `${branding.company_name} | AI-powered Customer Service & Sales platform`;
            }
        }

        // Cleanup function to remove favicon when component unmounts
        return () => {
            const links = document.querySelectorAll("link[rel*='icon']");
            links.forEach(link => link.remove());
            // Reset title to default if component unmounts
            document.title = 'AI-powered Customer Service & Sales platform';
        };
    }, [branding?.favicon_url, branding?.company_name, location.pathname, workflowTitle]);

    return null;
} 